import React, { ComponentType, SVGProps } from 'react';

import {
  ConsumerIcon,
  DrivetrainFilterIcon,
  EngineFilterIcon,
  FilterCalendarIcon,
  FilterMakeIcon,
  FilterModelIcon,
  FilterOdometerIcon,
  OrganizationIcon,
  SaveMediumIcon,
  ShareIcon,
  TransmissionFilterIcon,
} from '../assets';
import BaseFilterChip from '../components/filters/BaseFilterChip';
import MakeFilterChip from '../components/filters/MakeFilterChip';
import OrganizationsFilterChip from '../components/filters/OrganizationsFilterChip';
import RangeFilterChip from '../components/filters/RangeFilterChip';
import SelectionFilterChip from '../components/filters/SelectionFilterChip';
import TitleFilterChip from '../components/filters/TitleFilterChip';

/**
 * @description Includes custom props that can be passed to the icon component
 */
interface IconProps {
  fill?: string;
}

export default interface Filter {
  name: string;
  attributeKey: string;
  attributeText: string;
  getName(): string;
  getSelectedValues(): any;
  getQuery(): any;
  getChips(): React.ReactNode;
  getIcon(props?: IconProps): React.ReactNode;
  printValues(): string;
}

// TODO: https://trupropel.atlassian.net/browse/VC-600
export const MODEL_FILTER_ATTRIBUTE_KEY_PREFIX = 'ModelFilter_';

export enum FilterAttributeKey {
  PRICE = 'vehicle.price',
  ODOMETER = 'vehicle.odometer',
  YEAR = 'vehicle.year',
  MAKE = 'vehicle.make',
  MODEL = 'vehicle.model',
  TRANSMISSION = 'vehicle.transmission',
  FUEL = 'vehicle.fuel',
  DRIVETRAIN = 'vehicle.drivetrain',
  ENGINE = 'vehicle.engine',
  ORGANIZATIONS = 'receivingOrganizationId',
  SHAREDWITHUSER = 'shared',
  SAVEDLISTING = 'saved',
  CONSUMER = 'listingType',
}

export enum FilterNamePrefix {
  YearFilter = 'YearFilter',
  MakeFilter = 'MakeFilter',
  ModelFilter = 'ModelFilter',
  PriceFilter = 'PriceFilter',
  OdometerFilter = 'OdometerFilter',
  TransmissionFilter = 'TransmissionFilter',
  FuelFilter = 'FuelFilter',
  DrivetrainFilter = 'DrivetrainFilter',
  EngineFilter = 'EngineFilter',
  OrganizationsFilter = 'OrganizationsFilter',
  SharedWithUserFilter = 'SharedWithUserFilter',
  SavedListingFilter = 'SavedListingFilter',
  ConsumerFilter = 'ConsumersFilter',
}

export class BaseFilterComponent implements Filter {
  constructor(public name: string, public attributeKey: FilterAttributeKey, public attributeText: string) {}

  getName(): string {
    return this.name;
  }

  getSelectedValues(): any {
    return null;
  }

  getQuery(): any {
    return {};
  }

  getChips(): React.ReactNode {
    return <BaseFilterChip filter={this} text={''} />;
  }

  getIcon(props: SVGProps<SVGSVGElement>): React.ReactNode {
    let IconComponent: ComponentType<SVGProps<SVGSVGElement>> = undefined;
    switch (this.attributeKey) {
      case FilterAttributeKey.YEAR:
        IconComponent = FilterCalendarIcon;
        break;
      case FilterAttributeKey.MAKE:
        IconComponent = FilterMakeIcon;
        break;
      case FilterAttributeKey.ODOMETER:
        IconComponent = FilterOdometerIcon;
        break;
      case FilterAttributeKey.TRANSMISSION:
        IconComponent = TransmissionFilterIcon;
        break;
      case FilterAttributeKey.DRIVETRAIN:
        IconComponent = DrivetrainFilterIcon;
        break;
      case FilterAttributeKey.ENGINE:
        IconComponent = EngineFilterIcon;
        break;
      case FilterAttributeKey.ORGANIZATIONS:
        IconComponent = OrganizationIcon;
        break;
      case FilterAttributeKey.SHAREDWITHUSER:
        IconComponent = ShareIcon;
        break;
      case FilterAttributeKey.SAVEDLISTING:
        IconComponent = SaveMediumIcon;
        break;
      case FilterAttributeKey.CONSUMER:
        IconComponent = ConsumerIcon;
        break;
      default:
        // TODO: https://trupropel.atlassian.net/browse/VC-600
        if (this.attributeKey.indexOf(MODEL_FILTER_ATTRIBUTE_KEY_PREFIX) > -1) {
          IconComponent = FilterModelIcon;
        }
    }

    return IconComponent && <IconComponent {...props} />;
  }

  printValues(): string {
    return '';
  }

  removeFn(values?: any) {
    return values;
  }
}

interface RangeFilterArgs {
  start: number;
  end: number;
  unit?: string;
}

export class BaseRangeFilter extends BaseFilterComponent {
  constructor(
    name: string,
    attributeKey: FilterAttributeKey,
    attributeText: string,
    public start: number,
    public end: number,
    public unit?: string,
  ) {
    super(name, attributeKey, attributeText);
  }

  printValues(): string {
    switch (true) {
      case !!this.start && !this.end:
        return `> ${this.start}${this.unit || ''}`;
      case !!this.start && !!this.end:
        return `${this.start}${this.unit || ''}-${this.end}${this.unit || ''}`;
      case !!this.end && !this.start:
        return `< ${this.end}${this.unit || ''}`;
      default:
        return '';
    }
  }

  getSelectedValues(): any {
    return !this.start && !this.end ? [] : [this.start, this.end];
  }

  getQuery(): any {
    return { numberFilter: { field: this.attributeKey, operator: { between: { from: this.start, to: this.end } } } };
  }

  getChips(): React.ReactNode {
    return <RangeFilterChip filter={this} />;
  }
}

interface SelectionFilterArgs {
  values: string[];
}

export class BaseSelectionFilter extends BaseFilterComponent {
  constructor(name: string, attributeKey: FilterAttributeKey, attributeText: string, public values: string[]) {
    super(name, attributeKey, attributeText);
  }

  getSelectedValues(): string[] {
    return this.values;
  }

  printValues(): string {
    return this.values?.join(', ');
  }

  getQuery(): any {
    return { stringFilter: { field: this.attributeKey, operator: { eq: this.values } } };
  }

  getChips(): React.ReactNode {
    return this.values?.map((value) => <SelectionFilterChip filter={this} value={value} />);
  }

  removeFn(value?: any) {
    this.values = this.values.filter((item) => item !== value);
  }
}

export class BaseBooleanFilter extends BaseFilterComponent {
  constructor(name: string, attributeKey: FilterAttributeKey, attributeText: string, public value: boolean) {
    super(name, attributeKey, attributeText);
  }

  getSelectedValues(): boolean {
    return this.value;
  }

  getQuery(): any {
    return { booleanFilter: { field: this.attributeKey, operator: { eq: this.value } } };
  }

  getChips(): React.ReactNode {
    return <TitleFilterChip filter={this} value={this.value} />;
  }

  removeFn() {
    this.value = null;
  }
}

export class BaseStringFilter extends BaseFilterComponent {
  constructor(name: string, attributeKey: FilterAttributeKey, attributeText: string, public strValue: string) {
    super(name, attributeKey, attributeText);
  }

  getSelectedValues(): string {
    return this.strValue;
  }

  printValues(): string {
    return this.strValue;
  }

  getQuery(): any {
    return { stringFilter: { field: this.attributeKey, operator: { eq: this.strValue } } };
  }

  getChips(): React.ReactNode {
    return <TitleFilterChip filter={this} value={this.strValue} />;
  }

  removeFn() {
    this.strValue = null;
  }
}

interface BooleanFilterArgs {
  value: boolean;
}

interface StringFilterArgs {
  strValue: string;
}

export class PriceFilter extends BaseRangeFilter {
  constructor({ start, end, unit }: RangeFilterArgs) {
    super(FilterNamePrefix.PriceFilter, FilterAttributeKey.PRICE, 'Price', start, end, unit);
  }
}

export class OdometerFilter extends BaseRangeFilter {
  constructor({ start, end, unit }: RangeFilterArgs) {
    super(FilterNamePrefix.OdometerFilter, FilterAttributeKey.ODOMETER, 'Odometer', start, end, unit);
  }

  getQuery(): any {
    return {
      odometerFilter: {
        field: this.attributeKey,
        operator: { between: { from: this.start, to: this.end } },
        unit: this.unit?.toUpperCase(),
      },
    };
  }
}

export class YearFilter extends BaseRangeFilter {
  constructor({ start, end }: RangeFilterArgs) {
    super(FilterNamePrefix.YearFilter, FilterAttributeKey.YEAR, 'Year', start, end);
  }
}

export class MakeFilter extends BaseSelectionFilter {
  constructor({ values }: SelectionFilterArgs) {
    super(FilterNamePrefix.MakeFilter, FilterAttributeKey.MAKE, 'Make', values);
  }

  getChips(): React.ReactNode {
    return this.values?.map((value) => <MakeFilterChip filter={this} value={value} />);
  }
}
interface ModelSelectionFilterArgs {
  values: string[];
  parentKeyValue: string;
}

export class ModelFilter extends BaseSelectionFilter {
  parentKeyValue: string;

  constructor({ values, parentKeyValue }: ModelSelectionFilterArgs) {
    // TODO: https://trupropel.atlassian.net/browse/VC-600
    super(
      FilterNamePrefix.ModelFilter,
      `${MODEL_FILTER_ATTRIBUTE_KEY_PREFIX}${parentKeyValue}` as FilterAttributeKey,
      `${parentKeyValue} Models`,
      values,
    );
    this.parentKeyValue = parentKeyValue;
  }

  getParentKeyValue(): string {
    return this.parentKeyValue;
  }
}

export class TransmissionFilter extends BaseSelectionFilter {
  constructor({ values }: SelectionFilterArgs) {
    super(FilterNamePrefix.TransmissionFilter, FilterAttributeKey.TRANSMISSION, 'Transmission', values);
  }
}

export class FuelFilter extends BaseSelectionFilter {
  constructor({ values }: SelectionFilterArgs) {
    super(FilterNamePrefix.FuelFilter, FilterAttributeKey.FUEL, 'Fuel', values);
  }
}

export class DrivetrainFilter extends BaseSelectionFilter {
  constructor({ values }: SelectionFilterArgs) {
    super(FilterNamePrefix.DrivetrainFilter, FilterAttributeKey.DRIVETRAIN, 'Drivetrain', values);
  }
}

export class EngineFilter extends BaseSelectionFilter {
  constructor({ values }: SelectionFilterArgs) {
    super(FilterNamePrefix.EngineFilter, FilterAttributeKey.ENGINE, 'Engine', values);
  }
}

export class OrganizationsFilter extends BaseSelectionFilter {
  constructor({ values }: SelectionFilterArgs) {
    super(FilterNamePrefix.OrganizationsFilter, FilterAttributeKey.ORGANIZATIONS, 'Organizations', values);
  }

  getChips(): React.ReactNode {
    return <OrganizationsFilterChip key={this.attributeKey} filter={this} />;
  }
}

export class SharedWithUserFilter extends BaseBooleanFilter {
  constructor({ value }: BooleanFilterArgs) {
    super(FilterNamePrefix.SharedWithUserFilter, FilterAttributeKey.SHAREDWITHUSER, 'Shared with me', value);
  }
}

export class SavedListingFilter extends BaseBooleanFilter {
  constructor({ value }: BooleanFilterArgs) {
    super(FilterNamePrefix.SavedListingFilter, FilterAttributeKey.SAVEDLISTING, 'Saved', value);
  }
}

export class ConsumerFilter extends BaseStringFilter {
  constructor({ strValue }: StringFilterArgs) {
    super(FilterNamePrefix.ConsumerFilter, FilterAttributeKey.CONSUMER, 'Consumers', strValue);
  }
}

export type PossibleFilters = Filter &
  RangeFilterArgs &
  SelectionFilterArgs &
  ModelSelectionFilterArgs &
  BooleanFilterArgs &
  StringFilterArgs;
